// Single source of truth for the review/correction validation signals shown
// on DocumentViewPage (edit mode) and the UploadDocumentPage review stage.
//
// These two screens render near-identical correction tables and MUST stay in
// lockstep. Historically each page inlined its own copy of the row analysis
// (line-total mismatch, price-change badge, discount-agreement mismatch);
// this module centralizes that analysis so the inline cell flags AND the
// consolidated issue-summary strip derive from the exact same computation —
// they cannot diverge.
//
// Nothing here introduces a new rule. Every predicate is a verbatim move of
// the logic that previously lived inline in the two pages:
//   • rowMismatched        — stored line_total drifted from qty × net price
//   • price change         — unit_price differs from the catalog anchor
//                            (fixed_price when set, else last_price)
//   • discount mismatch    — per-item / invoice-level agreed discount not honored
//   • duplicate doc_num    — server-flagged duplicate_warning on bulk drafts
//   • implausible date     — OCR year far from now (dateSanity)
import { calcLineTotal, calcNetUnitPrice } from './lineCalc';
import { isImplausibleDateYear } from './dateSanity';
import { round2 } from './money';

const r2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

// Local currency formatter — deliberately NOT the one in utils/api.js so this
// module stays import-pure (api.js drags axios into every test that touches it).
const fmt = (n) => `₪${Number(n || 0).toFixed(2)}`;

// Plain invoice types that may silently be acting as a מרכזת — mirrors
// useOpenDeliveryNotes, which only asks the server for these.
export const OPEN_NOTES_DOC_TYPES = ['חשבונית', 'חשבונית/קבלה'];

// ['08/2026','09/2026'] → '08–09/2026'; across a year → '12/2025–01/2026'.
// Wrapped in an LTR isolate (U+2066…U+2069): inside Hebrew text the bidi
// algorithm otherwise flips the two numbers around the dash ('09/2026–08').
const monthSpan = (months = []) => {
  if (months.length < 2) return months[0] || '';
  const first = months[0], last = months[months.length - 1];
  const span = first.slice(3) === last.slice(3) ? `${first.slice(0, 2)}–${last}` : `${first}–${last}`;
  return `\u2066${span}\u2069`;
};

// Compares the stored line_total with qty × net price, in three tiers:
//   'rounding' — within what 2-decimal printing can explain. Suppliers bill by
//                the true weight (10.203 kg) but print 10.20, so the printed
//                qty × price misses by up to 0.005 × price; likewise a rounded
//                unit price misses by 0.005 × qty. Plus 1 agora on the total.
//                Never less than the old flat 0.05.
//   'warn'     — beyond rounding but no more than 1₪, whatever the line size.
//                Surfaced, doesn't block.
//   'block'    — anything larger. AI-extracted rows the user never audited
//                can arrive inconsistent, and those silently feed false
//                price-change reports downstream (the analysis NET_PRICE
//                prefers line_total / qty) — so these still stop the save.
export function lineTotalCheck(it) {
  const qty   = parseFloat(it.qty)        || 0;
  const total = parseFloat(it.line_total) || 0;
  const expected = calcLineTotal(it);
  const diff = Math.abs(expected - total);
  const tolerance = Math.max(0.05, 0.005 * (Math.abs(calcNetUnitPrice(it)) + Math.abs(qty)) + 0.01);
  const warnLimit = 1;
  let level = 'ok';
  if (qty <= 0 && total <= 0) level = 'ok'; // empty / credit rows were never checked
  else if (diff > warnLimit && diff > tolerance) level = 'block';
  else if (diff > tolerance) level = 'warn';
  else if (diff >= 0.005) level = 'rounding';
  return { level, expected, diff: r2(diff), tolerance };
}

// True iff the row's total mismatch is large enough to block saving.
export function rowMismatched(it) {
  return lineTotalCheck(it).level === 'block';
}

// Full per-row analysis used by the correction tables' inline flags and by
// deriveReviewIssues below. Anchor for price change: fixed_price when set
// (the agreed price never moves until the user edits the catalog), otherwise
// the catalog's last_price snapshot. Both rounded to 1-agora precision.
export function analyzeReviewRow(it) {
  const totalCheck    = lineTotalCheck(it);
  const mismatched    = totalCheck.level === 'block';
  const totalWarn     = totalCheck.level === 'warn';
  const expectedTotal = mismatched || totalWarn ? totalCheck.expected : null;

  const curPrice   = r2(it.unit_price);
  const fixedPrice = it.catalog_fixed_price ? r2(it.catalog_fixed_price) : null;
  const lastPrice  = it.catalog_last_price  ? r2(it.catalog_last_price)  : null;
  const anchorPrice   = fixedPrice && fixedPrice > 0 ? fixedPrice : lastPrice;
  const anchorIsFixed = !!(fixedPrice && fixedPrice > 0);
  const priceChanged  = anchorPrice != null && anchorPrice > 0 && curPrice > 0 && anchorPrice !== curPrice;
  const overcharge = anchorIsFixed && curPrice > fixedPrice ? r2(curPrice - fixedPrice) : 0;
  const creditDiffSet = (parseFloat(it.credit_price_diff) || 0) > 0;

  // Per-item discount agreement enforcement. Mirror of the price-change
  // pattern: anchor = catalog.fixed_discount, flag whenever the doc's
  // discount or type don't match. Rows without catalog enrichment (e.g. the
  // upload flow, which doesn't fetch fixed discounts) simply never flag.
  const agreedDiscount     = it.catalog_fixed_discount > 0 ? it.catalog_fixed_discount : null;
  const agreedDiscountType = it.catalog_fixed_discount_type || 'percent';
  const itDiscount     = parseFloat(it.discount) || 0;
  const itDiscountType = it.discount_type || 'percent';
  const discountMismatch = agreedDiscount != null && (
    itDiscount !== agreedDiscount || itDiscountType !== agreedDiscountType
  );
  // Per-unit lost amount when the actual discount is worse than agreed (only
  // same-type comparisons; mixed types surface as mismatch but no auto-credit).
  const sameType = agreedDiscount != null && itDiscountType === agreedDiscountType;
  const discountOvercharge = (() => {
    if (!sameType || itDiscount >= agreedDiscount) return 0;
    if (agreedDiscountType === 'percent') {
      return r2(curPrice * ((agreedDiscount - itDiscount) / 100));
    }
    return r2(agreedDiscount - itDiscount); // amount per unit
  })();

  return {
    mismatched, totalWarn, expectedTotal,
    curPrice, fixedPrice, lastPrice, anchorPrice, anchorIsFixed, priceChanged, overcharge, creditDiffSet,
    agreedDiscount, agreedDiscountType, discountMismatch, discountOvercharge,
  };
}

// Invoice-level discount agreement check (supplier.fixed_invoice_discount).
// Same semantics both pages used inline: agreed must be > 0 to apply; mismatch
// on value (±0.001) or on type.
export function invoiceDiscountMismatch({ agreed, agreedType, current, currentType }) {
  const a = parseFloat(agreed) || 0;
  if (!(a > 0)) return false;
  const cur = parseFloat(current) || 0;
  return Math.abs(cur - a) > 0.001 || (currentType || 'percent') !== (agreedType || 'percent');
}

// Derives the consolidated issue list for the summary strip. Each issue
// carries a targetId matching the id= attribute on the offending field so the
// strip can click-to-focus. Blocking issues (severity 'error') first —
// exactly the ones the existing save/approve guards reject.
// ── The printed totals (#314) ────────────────────────────────────────────────
// The edit form recomputes subtotal, VAT and total from the lines when a draft
// opens, so a document whose printed figures disagree with its lines looked
// fine. totalsCheck compares the form with the money as PRINTED (the
// documents.printed_* columns, copied once from the AI's reading — server
// lib/printedTotals.js) and says where to look, with one honest fix where one
// exists. Owner, 21/10/2026: amber only, never blocking; only for a difference
// of more than ₪0.02 either way; invoices and delivery notes only for now.
export const TOTALS_TOLERANCE = 0.02;
export const TOTALS_CHECK_TYPES = Object.freeze(['חשבונית', 'חשבונית/קבלה', 'חשבון עסקה', 'תעודת משלוח']);
// Up to this gap the honest fix is rounding: the VAT or the discount line (roundingFixes).
const VAT_FIT_MAX = 1;
const ROUNDING_PERCENT_MAX = 0.1;

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
// Money is compared in whole agorot: 32.02 − 31.02 is 1.0000000000000036 in
// floating point, which put a ₪1 rounding gap on the wrong side of the line
// and looped the VAT and total chips (Codex review of #319).
const agorot = (n) => Math.round(n * 100);
const gapAgorot = (a, b) => Math.abs(agorot(a) - agorot(b));
const off = (a, b) => gapAgorot(a, b) > agorot(TOTALS_TOLERANCE);
const discountAmount = (subtotal, discount, type) => {
  const d = num(discount) || 0;
  return type === 'amount' ? d : (num(subtotal) || 0) * d / 100;
};
// The VAT finding's test: a gap over the ₪1 of per-line rounding; a partial-VAT
// supplier only above the full rate.
const vatPastSlack = (vat, calcVat, vatRule) => gapAgorot(vat, calcVat) > agorot(VAT_FIT_MAX) && (!vatRule.partial || vat > calcVat);
const hasCredit = (it) => (num(it?.credit_qty) || 0) > 0 || (num(it?.credit_price_diff) || 0) > 0;

// A total off by up to ₪1 is rounding, and either the VAT absorbs it (a
// supplier rounding VAT per line) or the discount line does (a supplier
// rounding the total: 89.00 printed on 75.00 + 13.58 shows −0.42 under הנחה).
// Both are offered; the discount first when the VAT already is the printed
// one — the gap is not in it (owner, 31/10/2026). The discount fix writes ₪,
// and only over no discount or a rounding-size one: ≤ ₪1 and, in %, a rate under
// 0.1% (917.00 printed on 777.00 reads as "0.02%", #351). Over a real % it would
// lose the rate, over a real ₪ discount it would break a supplier's agreed one.
function roundingFixes({ form, subtotal, taxable, vat, total, pTotal, pVat, rate, vatRule }) {
  if (gapAgorot(total, pTotal) > agorot(VAT_FIT_MAX) || vatRule.inclusive) return [];
  const fixes = [];
  if (vat > 0) fixes.push({ kind: 'vat-fit', label: 'התאם מע"מ', vat: round2(pTotal - taxable) });
  const discount = round2(subtotal + round2(vat) - pTotal) || 0;
  const current = num(form.invoice_discount) || 0;
  const worth = agorot(round2(Math.abs(discountAmount(subtotal, current, form.invoice_discount_type))));
  const roundingOnly = !current || (worth <= agorot(VAT_FIT_MAX) && (form.invoice_discount_type === 'amount' || Math.abs(current) < ROUNDING_PERCENT_MAX));
  // Over a %, not a fix that opens a VAT chip whose own fix leaves the total
  // past this one's reach (Codex review of #352); התאם מע"מ stays on offer.
  const overPercent = current && form.invoice_discount_type !== 'amount';
  const opensVatChip = overPercent && rate > 0 && off(vat, 0) && vatPastSlack(vat, round2((subtotal - discount) * rate / 100), vatRule);
  if (roundingOnly && !opensVatChip && off(discount, subtotal - taxable)) {
    fixes.push({ kind: 'discount-fit', label: 'התאם הנחה', discount });
  }
  if (fixes.length === 2 && pVat != null && !off(vat, pVat)) fixes.reverse();
  return fixes;
}

/**
 * Every finding comparing the form with the printed money, in the order the
 * strip would offer them: lines vs subtotal, discount, VAT, total. `printed`:
 * { subtotal, invoice_discount, invoice_discount_type, vat, total } — any
 * field null when not read. `vatRule`: { inclusive, partial } (utils/vatRule.js).
 * Empty when the check does not run. The stage-3 gate reports each one
 * (server/lib/reviewChecks.js, a sync pair); the strip shows the first
 * (totalsCheck below).
 */
export function totalsFindings({ printed, form = {}, items = [], docType, vatRate = 0, vatRule = {} } = {}) {
  const pTotal = num(printed?.total);
  if (pTotal == null || !TOTALS_CHECK_TYPES.includes(docType)) return [];
  const lines = Array.isArray(items) ? items : [];
  // No lines to compare, a credit line (the document goes לא תקין anyway), or
  // prices the server filled in (a note with no printed prices) — no check.
  if (lines.length === 0 || lines.some(hasCredit) || lines.some(it => num(it?.price_estimated))) return [];

  const subtotal = num(form.subtotal) || 0;
  const vat = num(form.vat) || 0;
  const total = num(form.total) || 0;
  const taxable = subtotal - discountAmount(subtotal, form.invoice_discount, form.invoice_discount_type);
  const rate = num(vatRate) || 0;
  // The form's own formula (DocumentViewPage, decimal-safe round2): the float
  // r2 rounded 4.185 down to 4.18, one agora off what חשב לפי 18% writes, and
  // that agora looped the VAT and total chips at the ₪1 line (Codex round 2).
  const calcVat = round2(taxable * rate / 100);
  const base = { id: 'totals', type: 'totals', severity: 'warning' };
  const found = [];

  // 1. The lines don't add up to the printed subtotal: look at the lines.
  const pSub = num(printed.subtotal);
  if (pSub != null && off(subtotal, pSub)) {
    found.push({ ...base, check: 'subtotal', label: `סכום השורות ${subtotal < pSub ? 'נמוך' : 'גבוה'} ב-${fmt(Math.abs(subtotal - pSub))} מהמסמך`, targetId: 'ri-items' });
  }
  // 2. The discount entered isn't the printed one.
  const pDisc = num(printed.invoice_discount);
  if (pDisc != null) {
    const pType = printed.invoice_discount_type === 'amount' ? 'amount' : 'percent';
    const formDisc = discountAmount(subtotal, form.invoice_discount, form.invoice_discount_type);
    const printedDisc = discountAmount(subtotal, pDisc, pType);
    // A discount under ₪1 from the printed one that lands the total on the
    // printed total is the supplier rounding the total through the discount
    // line (התאם הנחה below) — the AI often reads that line's "0.00 %"
    // label instead of its amount (owner, 31/10/2026).
    const roundingDisc = !off(total, pTotal) && gapAgorot(formDisc, printedDisc) <= agorot(VAT_FIT_MAX);
    if (off(formDisc, printedDisc) && !roundingDisc) {
      found.push({ ...base, check: 'discount', label: `הנחת חשבונית לא תואמת למסמך (במסמך ${pType === 'amount' ? fmt(pDisc) : `${pDisc}%`})`, targetId: 'ri-invoice_discount' });
    }
  }
  // 3. VAT, unless the supplier's prices include it.
  if (!vatRule.inclusive && rate > 0) {
    // No אוטו offer for a partial-VAT supplier: the form keeps its typed VAT
    // whatever the mode, so the tap would only hide the field (code review).
    const vatAction = vatRule.partial ? null : { kind: 'vat-auto', label: `חשב לפי ${rate}%` };
    if (!off(vat, 0)) {
      // 0 VAT is right on a VAT-free document; it's a misread only when the
      // VAT really is missing and the printed total is the taxable amount
      // PLUS the rate — not the taxable amount alone (a few-agorot document
      // fits both; Codex review of #319).
      if (off(vat, calcVat) && off(pTotal, taxable) && !off(pTotal, round2(taxable + calcVat))) {
        found.push({ ...base, check: 'vat_missing', label: 'חסר מע"מ — הסה"כ במסמך כולל מע"מ', targetId: 'ri-vat', ...(vatAction && { action: vatAction }) });
      }
    } else if (vatPastSlack(vat, calcVat, vatRule)) {
      // A gap of up to ₪1 is the supplier rounding VAT per line — not worth
      // amber; if the total is then off, the total chip's התאם מע"מ absorbs
      // it (design + code review of #314: flagging both looped).
      // The printed VAT stays (owner, 21/10/2026); the calculation sits
      // beside it, applied only by a tap. A partial-VAT supplier is flagged
      // only above the full rate.
      found.push({ ...base, check: 'vat', label: `המע"מ ${vat < calcVat ? 'נמוך' : 'גבוה'} ב-${fmt(Math.abs(vat - calcVat))} מחישוב ${rate}%`, targetId: 'ri-vat', ...(vatAction && { action: vatAction }) });
    }
  }
  // 4. The total. A small gap is rounding, for the VAT or the discount to absorb.
  if (off(total, pTotal)) {
    const gap = Math.abs(total - pTotal);
    const label = `הסה"כ ${total < pTotal ? 'נמוך' : 'גבוה'} ב-${fmt(gap)} מהמסמך`;
    const actions = roundingFixes({ form, subtotal, taxable, vat, total, pTotal, pVat: num(printed.vat), rate, vatRule });
    found.push({ ...base, check: 'total', label, targetId: 'ri-total', ...(actions.length && { actions }) });
  }
  return found;
}

/** The strip's one totals issue (or null): the first of totalsFindings. */
export function totalsCheck(args) {
  return totalsFindings(args)[0] || null;
}

export function deriveReviewIssues({
  items = [],
  form = {},
  duplicateWarning = false,
  duplicateOf = null,          // { id } of the conflicting doc, for a compare action
  supplierMissing = false,
  recipientMismatch = false,   // doc's extracted recipient ≠ current restaurant
  recipientName = '',
  canAddAlias = false,         // admin: offer "add as alternative name" on the chip
  // 'bid' when the ח.פ. comparison decided the mismatch (then an alias can't
  // help — the fix is "this ח.פ. is the supplier's"), 'name' otherwise.
  recipientDecidedBy = 'name',
  recipientBusinessId = null,  // the ח.פ. read as the recipient's
  restaurantBusinessId = null,
  // The server's move_suggestion (GET /documents/:id, #304): the recipient
  // points at another restaurant. Admin only; then the chip's one action is
  // the move — a document that matches another customer is not an alias of
  // this one, and its ח.פ. is not the supplier's.
  moveSuggestion = null,
  // Selected supplier's uses_delivery_notes: true/false, or undefined when
  // unknown (supplier list not loaded) — undefined never warns.
  supplierUsesDeliveryNotes,
  canMarkDeliveryNotes = false,
  // { count, months } — the supplier's open תעודות משלוח in this
  // invoice's window (server openNotesForInvoice); null when unknown.
  openDeliveryNotes = null,
  canConvertToMerkaz = false,  // admin: the link modal the retype leads to is admin-only
  agreedInvoiceDiscount = null,
  agreedInvoiceDiscountType = 'percent',
  // The money as printed and what the check needs to judge it (#314).
  printed = null,
  vatRate = 0,
  vatRule = {},
} = {}) {
  const errors = [];
  const warnings = [];

  if (supplierMissing) {
    errors.push({
      id: 'supplier', type: 'supplier', severity: 'error',
      label: 'לא נבחר ספק — חובה לבחור ספק לפני שמירה/אישור',
      targetId: 'ri-supplier',
    });
  }
  if (isImplausibleDateYear(form.date)) {
    errors.push({
      id: 'date', type: 'date', severity: 'error',
      label: `השנה בתאריך (${String(form.date || '').trim().slice(-4)}) חריגה — חוסם שמירה`,
      targetId: 'ri-date',
    });
  }
  if (duplicateWarning) {
    errors.push({
      id: 'duplicate', type: 'duplicate', severity: 'error',
      label: `מספר מסמך ${form.doc_num || ''} כבר קיים אצל הספק — כפיל, חוסם אישור`,
      targetId: 'ri-doc_num',
      // Secondary action: the compare dialog (DuplicateCompareDialog) over
      // the form, so the admin decides without leaving the draft (#234). It
      // used to navigate to the existing document and lose the edit.
      action: duplicateOf?.id ? { kind: 'compare', label: 'השווה זה ליד זה' } : undefined,
    });
  }
  // Recipient mismatch is a soft warning (the operator may legitimately keep
  // it), folded into the strip so it's the single source of truth instead of a
  // large standalone red banner that contradicted the green "all clear" line.
  const moveAction = canAddAlias && moveSuggestion ? { kind: 'move', label: 'העבר למסעדה' } : null;
  // With the move on offer the chip says where the draft belongs, not which
  // two numbers differ — it is tap one of two (design review, 31/10/2026).
  const joinedName = (n) => (/^[\u0590-\u05FF]/.test(n) ? n : `-${n}`);
  const moveLabel = !moveAction ? null
    : moveSuggestion.confident && moveSuggestion.name
      ? `המסמך נראה שייך ל${joinedName(moveSuggestion.name)}${recipientBusinessId ? ` · ח.פ. הנמען ${recipientBusinessId}` : ''}`
      : `${recipientBusinessId ? `ח.פ. הנמען (${recipientBusinessId})` : 'שם הנמען'} שייך למסעדה אחרת — יש לבחור סניף`;
  if (recipientMismatch && recipientDecidedBy === 'bid' && recipientBusinessId) {
    warnings.push({
      id: 'recipient', type: 'recipient', severity: 'warning',
      label: moveLabel || `הח.פ. שנקרא כח.פ. הנמען (${recipientBusinessId}) שונה מהח.פ. של המסעדה${restaurantBusinessId ? ` (${restaurantBusinessId})` : ''}`,
      targetId: 'ri-supplier',
      action: moveAction || (canAddAlias
        ? { kind: 'ignore-bid', label: 'זה הח.פ. של הספק', bid: recipientBusinessId, name: recipientName.trim() }
        : undefined),
    });
  } else if (recipientMismatch) {
    warnings.push({
      id: 'recipient', type: 'recipient', severity: 'warning',
      label: moveLabel || `שם הנמען במסמך לא תואם למסעדה${recipientName ? ` — נראה שייך ל"${recipientName}"` : ''}`,
      targetId: 'ri-supplier',
      action: moveAction || (canAddAlias && recipientName.trim()
        ? { kind: 'add-alias', label: 'הוסף כשם חלופי', name: recipientName.trim() }
        : undefined),
    });
  }

  // A delivery note from a supplier not flagged "עובד עם תעודות משלוח" is left
  // out of ניתוח רכש / מעקב ספקים until the flag is set (auto only at 3
  // verified notes), so say so where the admin can fix it in one click.
  if (form.doc_type === 'תעודת משלוח' && form.supplier_id && supplierUsesDeliveryNotes === false) {
    warnings.push({
      id: 'supplier-dn', type: 'supplier_dn', severity: 'warning',
      label: 'הספק לא מסומן כעובד עם תעודות משלוח — התעודה לא תיספר ברכש',
      targetId: 'ri-supplier',
      action: canMarkDeliveryNotes ? { kind: 'mark-delivery-notes', label: 'סמן את הספק' } : undefined,
    });
  }

  // A plain invoice while the supplier has open delivery notes: some suppliers
  // bill a batch of deliveries with a חשבונית that acts as a מרכזת, and since
  // only a מרכזת can be linked, invoice + notes would both count in full. The
  // fix is to retype it; saving then opens the existing link modal.
  if (OPEN_NOTES_DOC_TYPES.includes(form.doc_type) && form.supplier_id && openDeliveryNotes?.count > 0) {
    const { count, months = [] } = openDeliveryNotes;
    const what = count === 1 ? 'תעודת משלוח לא מקושרת' : `${count} תעודות משלוח לא מקושרות`;
    // Kept short: the chip truncates on one line and has no tooltip, and the
    // action button already says what to do.
    warnings.push({
      id: 'open-dn', type: 'open_dn', severity: 'warning',
      label: `${what} מ-${monthSpan(months)} — ייתכן שזו חשבונית מרכזת`,
      targetId: 'ri-doc-type',
      action: canConvertToMerkaz ? { kind: 'convert-to-merkaz', label: 'הפוך לחשבונית מרכזת' } : undefined,
    });
  }

  items.forEach((it, i) => {
    const a = analyzeReviewRow(it);
    if (a.mismatched) {
      errors.push({
        id: `row-${i}-total`, type: 'row_total', severity: 'error', row: i,
        label: `שורה ${i + 1}: סך הכל (${fmt(it.line_total)}) לא תואם כמות × מחיר נטו (${fmt(a.expectedTotal)}) — חוסם שמירה`,
        targetId: `ri-item-${i}-line_total`,
      });
    } else if (a.totalWarn) {
      warnings.push({
        id: `row-${i}-total`, type: 'row_total', severity: 'warning', row: i,
        label: `שורה ${i + 1}: סך הכל (${fmt(it.line_total)}) שונה מכמות × מחיר נטו (${fmt(a.expectedTotal)}) — בדקי`,
        targetId: `ri-item-${i}-line_total`,
      });
    }
    if (a.priceChanged) {
      warnings.push({
        id: `row-${i}-price`, type: 'price_change', severity: 'warning', row: i,
        label: `שורה ${i + 1}: ${a.anchorIsFixed ? 'סטייה ממחיר קבוע' : 'שינוי מחיר'} — היה ${fmt(a.anchorPrice)}, עכשיו ${fmt(a.curPrice)}`,
        targetId: `ri-item-${i}-unit_price`,
      });
    }
    if (a.discountMismatch) {
      warnings.push({
        id: `row-${i}-discount`, type: 'discount_mismatch', severity: 'warning', row: i,
        label: `שורה ${i + 1}: הנחה לא תואמת להסכם (מוסכם ${a.agreedDiscount}${a.agreedDiscountType === 'percent' ? '%' : '₪'})`,
        targetId: `ri-item-${i}-discount`,
      });
    }
  });

  if (invoiceDiscountMismatch({
    agreed: agreedInvoiceDiscount,
    agreedType: agreedInvoiceDiscountType,
    current: form.invoice_discount,
    currentType: form.invoice_discount_type,
  })) {
    warnings.push({
      id: 'invoice-discount', type: 'invoice_discount_mismatch', severity: 'warning',
      label: `הנחת חשבונית לא תואמת להסכם (מוסכם ${parseFloat(agreedInvoiceDiscount) || 0}${(agreedInvoiceDiscountType || 'percent') === 'percent' ? '%' : '₪'})`,
      targetId: 'ri-invoice_discount',
    });
  }

  const totals = totalsCheck({ printed, form, items, docType: form.doc_type, vatRate, vatRule });
  if (totals) warnings.push(totals);

  return [...errors, ...warnings];
}
